<template>
    <div
        class="dk-wizard-lead-form-modal dst max-lg:tw-min-h-screen max-lg:tw-relative lg:tw-h-full lg:tw-flex lg:tw-flex-col lg:tw-justify-between"
    >
        <div class="dk-wizard-lead-form-modal__content">
            <dk-wizard-lead-form-header
                :full-name="fullName"
                :has-dealer-selection="hasDealerSelection"
                :selected-dealer="selectedDealer"
                :selected-color="colorName"
                :version-name="versionName"
            />
            <div
                v-if="loading"
                class="dk-wizard-lead-form-modal__loader tw-flex tw-h-[480px] lg:tw-h-full tw-items-center tw-justify-center"
            >
                <dk-icon name="spinner" size="s" :is-spinning="true" />
            </div>
            <dk-wizard-lead-form-tabs
                v-show="isLeadFormMounted && !loading"
                :key="tabsKey"
                ref="wizardNavigation"
                @change="onTabChange"
            >
                <dk-tab-dst
                    v-if="hasOffers"
                    id="wizard_form__offers"
                    class="lg:tw-relative tw-h-full tw-px-6"
                    :class="{ 'max-lg:tw-pb-26': offers.length <= 2 }"
                    :name="$t('wizard-lead-form.offer-type')"
                >
                    <dk-wizard-lead-form-scroll-wrapper
                        :is-mobile="isMobile"
                        class="max-lg:tw-pb-26"
                        tab-id="wizard_form__offers"
                        :is-scroll-content-ready="!loading"
                        @has-scroll-changed="onScrollChanged"
                    >
                        <dk-wizard-lead-form-car-image
                            :class="{ 'lg:tw-mr-4': hasScroll('wizard_form__offers') }"
                            :image="colorImage"
                            :full-name="fullName"
                        />
                        <div
                            class="dk-wizard-lead-form-modal__offers tw-mt-8 lg:tw-mt-6"
                            :class="{ 'lg:tw-mr-4': hasScroll('wizard_form__offers') }"
                        >
                            <h6
                                class="dk-wizard-lead-form-modal__offers-title tw-mb-6 lg:tw-mb-4 tw-text-2xl tw-leading-lg tw-font-semibold tw-text-center"
                            >
                                {{ $t('wizard-lead-form.select-an-offer') }}
                            </h6>
                            <div
                                class="dk-wizard-lead-form-modal__offers-list tw-flex tw-flex-col tw-gap-2 lg:tw-gap-4"
                            >
                                <div
                                    v-for="(offer, index) in offers"
                                    :key="`offer-${_uid}-${index}`"
                                    class="dk-wizard-lead-form-modal__offer-item tw-flex tw-p-4 tw-border tw-border-solid tw-rounded"
                                    :class="getOfferClasses(offer)"
                                >
                                    <div
                                        class="dk-wizard-lead-form-modal__offer-input lg:tw-w-full tw-flex tw-items-center"
                                        @click="handleOfferSelection(index, offer)"
                                    >
                                        <input
                                            :id="`${offer.offerId}-${index}`"
                                            class="dk-wizard-lead-form-modal__offer-radio max-lg:!tw-mr-4 tw-shrink-0 checked:!tw-border-brand-primary"
                                            :checked="isOfferSelected(offer.offerId)"
                                            type="radio"
                                            :value="offer.finalPrice"
                                            name="wizard-lead-form-modal-offer"
                                        >
                                        <div
                                            class="dk-wizard-lead-form-modal__offer-radio-content lg:tw-w-full tw-flex tw-flex-col tw-gap-2 lg:tw-flex-row-reverse lg:tw-justify-between lg:tw-items-center"
                                        >
                                            <dk-badge
                                                class="dk-offer-card__badge tw-w-fit tw-gap-2"
                                                size="small"
                                                :type="isOfferSelected(offer.offerId) ? 'dark-blue' : 'light-blue'"
                                            >
                                                <span class="dk-offer-card__badge-text">{{ offer.label }}</span>
                                                <dk-icon
                                                    name="ds-tag-price-filled"
                                                    size="xs"
                                                    :color="isOfferSelected(offer.offerId) ? 'negative' : 'blue-700'"
                                                />
                                            </dk-badge>
                                            <label
                                                :for="`${offer.offerId}-${index}`"
                                                class="dk-wizard-lead-form-modal__offer-label tw-text-base lg:tw-text-lg tw-leading-sm lg:tw-leading-base tw-font-semibold"
                                            >
                                                <span class="dk-wizard-lead-form-modal__offer-price !tw-text-basic">
                                                    {{ offer.finalPrice }}
                                                    <span class="tw-text-base-subtle body-sm">
                                                        {{ $t('ds-vehicle-card.vat-included') }}
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    <template v-if="!isEmpty(offer.offerLogo)">
                                        <dk-divider class="tw-w-full" />
                                        <dk-partner-promo-label
                                            :is-partner-promo="!!offer.promo_id"
                                            :logo-data="offer.offerLogo"
                                            :tooltip-data="getTooltipData(offer)"
                                            is-simplified
                                        />
                                    </template>
                                </div>
                            </div>
                        </div>
                    </dk-wizard-lead-form-scroll-wrapper>
                </dk-tab-dst>
                <dk-tab-dst
                    id="wizard_form__color"
                    class="lg:tw-relative tw-h-full max-lg:tw-pb-26 lg:tw-px-6"
                    :name="colorTabName"
                    :disabled="isTabDisabled('wizard_form__color')"
                >
                    <dk-wizard-lead-form-scroll-wrapper
                        :is-mobile="isMobile"
                        class="max-lg:tw-mb-26"
                        tab-id="wizard_form__color"
                        :is-scroll-content-ready="!loading"
                        @has-scroll-changed="onScrollChanged"
                    >
                        <loading-wrapper :loading="loading">
                            <template v-if="areColorsLoaded">
                                <dk-wizard-lead-form-color-selection
                                    class="max-lg:tw-hidden"
                                    :colors="colors"
                                    :default-color="activeColor"
                                    :image="colorImage"
                                    :max-colors="9"
                                    :full-name="fullName"
                                    :has-scroll="hasScroll('wizard_form__color')"
                                    @color-change="onColorChange"
                                    @fake-color-select="onFakeColorSelection"
                                />
                                <dk-wizard-lead-form-color-selection
                                    class="lg:tw-hidden"
                                    :colors="colors"
                                    :default-color="activeColor"
                                    :image="colorImage"
                                    @color-change="onColorChange"
                                    @fake-color-select="onFakeColorSelection"
                                />
                            </template>
                        </loading-wrapper>
                        <div
                            v-if="isVersionPage"
                            class="dk-wizard-lead-form-modal__optionals tw-mt-8 lg:tw-mt-10"
                            :class="{ 'lg:tw-mb-5': hasScroll('wizard_form__color') }"
                        >
                            <h6
                                class="dk-wizard-lead-form-modal__optionals-title tw-mb-6 lg:tw-mb-4 tw-text-2xl tw-leading-lg tw-font-semibold tw-text-center"
                            >
                                {{ $t('wizard-lead-form.selected-optionals') }}
                            </h6>
                            <div class="dk-configuration-summary-box__pricing tw-flex tw-flex-col tw-px-6 lg:tw-px-20">
                                <div
                                    class="dk-configuration-summary-box__price-wrapper tw-flex tw-justify-between tw-items-center tw-px-2"
                                >
                                    <span
                                        class="dk-configuration-summary-box__price-title tw-text-xs tw-text-basic tw-leading-xs"
                                    >
                                        {{ $t('configuration-summery-box.listing-price') }}
                                    </span>
                                    <span
                                        class="dk-configuration-summary-box__price-value tw-text-xs tw-text-basic tw-leading-xs tw-font-bold"
                                    >
                                        {{ price }}
                                    </span>
                                </div>
                                <dk-divider class="tw-w-full lg:tw-mt-1 tw-mb-3" />
                                <div
                                    class="dk-configuration-summary-box__price-wrapper tw-flex tw-justify-between tw-items-center tw-px-2"
                                >
                                    <span
                                        class="dk-configuration-summary-box__price-title tw-text-xs tw-text-basic tw-leading-xs"
                                    >
                                        {{ $t('configuration-summery-box.optionals') }} ({{ optionals.count || '--' }})
                                    </span>
                                    <span
                                        class="dk-configuration-summary-box__price-value tw-text-xs tw-text-basic tw-leading-xs tw-font-bold"
                                    >
                                        {{ dkCurrency(optionals.price, $i18n.locale) }}
                                    </span>
                                </div>
                                <dk-divider class="tw-w-full lg:tw-mt-1 tw-mb-3" />
                                <template v-if="isRealColor">
                                    <div
                                        class="dk-configuration-summary-box__price-wrapper tw-flex tw-justify-between tw-items-center tw-px-2"
                                    >
                                        <span
                                            class="dk-configuration-summary-box__price-title tw-text-xs tw-text-basic tw-leading-xs"
                                        >
                                            {{ $t('configuration-summery-box.color') }}
                                        </span>
                                        <span
                                            class="dk-configuration-summary-box__price-value tw-text-xs tw-text-basic tw-leading-xs tw-font-bold"
                                        >
                                            +
                                            {{ dkCurrency(activeColor.basePrice, $i18n.locale) }}
                                        </span>
                                    </div>
                                    <dk-divider class="tw-w-full lg:tw-mt-1 tw-mb-3" />
                                </template>
                            </div>
                        </div>
                    </dk-wizard-lead-form-scroll-wrapper>
                </dk-tab-dst>
                <dk-tab-dst
                    v-if="hasDealerSelection"
                    id="wizard_form__dealer"
                    :name="$t('dealer-selection.dealer')"
                    :disabled="isTabDisabled('wizard_form__dealer')"
                >
                    <dk-dealer-input
                        class="lg:tw-mx-auto lg:tw-w-full lg:tw-max-w-[448px] lg:tw-mt-6 max-lg:tw-p-6"
                        :project-id="Number(projectId)"
                        is-wizard-form
                    />
                </dk-tab-dst>
                <dk-tab-dst
                    id="wizard_form__price"
                    :name="$t('model.version.price')"
                    :disabled="isTabDisabled('wizard_form__price')"
                >
                    <dk-wizard-lead-form-submission
                        :form-id="formId"
                        :form-data="formData"
                        :active-color="activeColor"
                        :make="make"
                        :model="model"
                        :promotion-id="promotionId"
                        :is-mobile="isMobile"
                    />
                </dk-tab-dst>
            </dk-wizard-lead-form-tabs>
        </div>
        <div
            class="dk-wizard-lead-form-modal__footer tw-w-full max-lg:tw-grid-cols-2 max-lg:tw-items-center max-lg:tw-gap-4 tw-py-4 tw-px-6 max-lg:tw-fixed max-lg:tw-bottom-0 max-lg:tw-left-0 max-lg:tw-z-10 max-lg:tw-w-full max-lg:tw-bg-negative max-lg:tw-shadow-md-up"
            :class="!isPriceTab ? 'lg:tw-py-6 max-lg:tw-grid' : 'max-lg:tw-hidden lg:tw-pb-6 lg:tw-pt-0'"
        >
            <div
                v-if="!isPriceTab"
                class="dk-wizard-lead-form-modal__footer-price tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-end lg:tw-justify-center lg:tw-gap-4 tw-text-basic"
            >
                <span class="dk-wizard-lead-form-modal__footer-price-prefix body-sm"> {{ pricePrefix }}: </span>
                <span
                    v-if="showPrice"
                    class="dk-wizard-lead-form-modal__footer-price-value tw-text-lg lg:tw-text-2xl tw-leading-base lg:tw-leading-lg tw-font-bold"
                >
                    {{ priceToShow }}
                </span>
                <span
                    v-else
                    class="dk-wizard-lead-form-modal__footer-price-placeholder tw-text-lg lg:tw-text-2xl tw-leading-base lg:tw-leading-lg tw-font-bold"
                >
                    -----------
                </span>
            </div>
            <dk-button
                class="dk-wizard-lead-form-modal__footer-button tw-w-full"
                :class="{ 'lg:tw-mt-4': !isPriceTab }"
                variant="accent"
                type="button"
                :disabled="isButtonDisabled"
                @click="onContinue"
            >
                {{ buttonText }}
            </dk-button>
        </div>
    </div>
</template>

<script>
/**
 * Three-step modal with dealer selection
 */
import { dkModalAware } from '@v4/common/components/mixins/modal';
import isEmpty from 'lodash/isEmpty';
import { fetchConfigurator } from '@v4/services/api/v3/car';
import { EventBus } from '@v4/common/utils/event-bus';
import generateTabMap from '@/js/common/components/organisms/DkWizardLeadFormModal/generate-tab-map';
import { dealerSelectionStore } from '@v4/lead-form/store/index';
import { DEALER_SELECTION_MUTATIONS } from '@v4/lead-form/store/mutations';
import DkWizardLeadFormScrollWrapper from '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormScrollWrapper.vue';
import { dkCurrency } from '@v4/common/filters';

export default {
    name: 'DkWizardLeadFormModal',
    components: {
        DkWizardLeadFormScrollWrapper,
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
        DkDealerInput: () => ({
            component: import(
                /* webpackChunkName: "DkDealerInput-vue" */ '@v4/common/components/molecules/DkDealerInput.vue'
            ),
        }),
        DkTabDst: () => ({
            component: import(/* webpackChunkName: "DkTabDST-vue" */ '@v4/common/components/atoms/DkTabDST.vue'),
        }),
        DkWizardLeadFormColorSelection: () => ({
            component: import(
                /* webpackChunkName: "DkWizardLeadFormColorSelection-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormColorSelection.vue'
            ),
        }),
        DkWizardLeadFormHeader: () => ({
            component: import(
                /* webpackChunkName: "DkWizardLeadFormHeader-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormHeader.vue'
            ),
        }),
        DkWizardLeadFormSubmission: () => ({
            component: import(
                /* webpackChunkName: "DkWizardLeadFormSubmission-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormSubmission.vue'
            ),
        }),
        DkWizardLeadFormTabs: () => ({
            component: import(
                /* webpackChunkName: "DkWizardLeadFormTabs-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormTabs.vue'
            ),
        }),
        LoadingWrapper: () => ({
            component: import(
                /* webpackChunkName: "LoadingWrapper-vue" */ '@v4/common/components/atoms/LoadingWrapper'
            ),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkWizardLeadFormCarImage: () => ({
            component: import(
                /* webpackChunkName: "DkWizardLeadFormCarImage-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal/components/DkWizardLeadFormCarImage.vue'
            ),
        }),
        DkBadge: () => ({
            component: import(/* webpackChunkName: "DkBadgeDST-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'),
        }),
        DkDivider: () => ({
            component: import(/* webpackChunkName: "DkDividerDST-vue" */ '@v4/common/components/atoms/DkDivider.vue'),
        }),
        DkPartnerPromoLabel: () => ({
            component: import(
                /* webpackChunkName: "DkPartnerPromoLabel-vue" */ '@v4/common/components/molecules/DkPartnerPromoLabel.vue'
            ),
        }),
    },
    mixins: [dkModalAware],
    props: {
        fullName: {
            type: String,
            default: '',
        },
        make: {
            type: String,
            default: '',
        },
        model: {
            type: String,
            default: '',
        },
        formId: {
            type: String,
            default: '',
        },
        formData: {
            type: Object,
            default: null,
        },
        selectedColor: {
            type: Object,
            default: () => ({}),
        },
        price: {
            type: String,
            default: '',
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
        showPriceTab: {
            type: Boolean,
            default: false,
        },
        versionName: {
            type: String,
            default: '',
        },
        offerType: {
            type: String,
            default: 'LISTING',
        },
        promotionId: {
            type: String,
            default: '',
        },
        offers: {
            type: Array,
            default: () => [],
        },
        setSelectedOffer: {
            type: Function,
            default: () => {},
        },
        optionals: {
            type: Object,
            default: () => ({}),
        },
        customCta: {
            type: String,
            default: null,
        },
        skipColorsTab: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeColor: this.selectedColor,
            activeTab: null,
            areColorsLoaded: false,
            colorImage: null,
            colors: [],
            currentColor: '',
            error: null,
            loading: true,
            isSubmitDisabled: true,
            modalHeight: null,
            isUpdatingTabs: false,
            isClickOnTab: false,
            isFormBusy: false,
            selectedOffer: null,
            showColorName: false,
            scrollStatus: {
                wizard_form__offers: false,
                wizard_form__color: false,
            },
            isClickOnContinue: false,
            isLeadFormMounted: false,
            hasDealerSelection: false,
        };
    },
    computed: {
        colorName() {
            return this.activeColor && (!this.isOffersTab || this.showColorNameOnOffersTab) && this.isLeadFormMounted
                ? this.activeColor.description
                : '';
        },
        showDealerInfo() {
            return (
                this.hasDealerSelection &&
                this.selectedDealer &&
                this.selectedDealer.name &&
                this.selectedDealer.address
            );
        },
        defaultImage() {
            return this.colors && this.colors.length ? this.colors[0].colorImage.image800 : this.colorImage;
        },
        tabKeys() {
            return Object.keys(this.tabs);
        },
        isPriceTab() {
            return this.activeTab === 'wizard_form__price';
        },
        isDealerTab() {
            return this.activeTab === 'wizard_form__dealer';
        },
        isOffersTab() {
            return this.activeTab === 'wizard_form__offers';
        },
        buttonText() {
            return this.isPriceTab
                ? this.customCta || this.$t('forms.cta.quote')
                : this.$t('configuration-wizard.continue');
        },
        tabs() {
            return generateTabMap(this.hasOffers, this.hasDealerSelection);
        },
        selectedDealer() {
            return dealerSelectionStore.state.selectedDealer;
        },
        isButtonDisabled() {
            return (
                (this.hasDealerSelection && !this.selectedDealer && this.activeTab === 'wizard_form__dealer') ||
                (this.isPriceTab && (this.isSubmitDisabled || this.isFormBusy)) ||
                (this.isOffersTab && !this.selectedOffer)
            );
        },
        pricePrefix() {
            let prefix;

            const offer = this.selectedOffer ? this.selectedOffer.type : this.offerType;

            switch (offer) {
                case 'LONG_TERM_RENTAL':
                    prefix = this.$t('price.long-term-rental-from');
                    break;
                case 'FINANCING':
                    prefix = this.$t('price.financing-from');
                    break;
                case 'LEASING':
                    prefix = this.$t('price.leasing-from');
                    break;
                case 'OFFERING':
                    prefix = this.$t('price.offering-from');
                    break;
                default:
                    prefix = this.$t('price.price-from');
                    break;
            }

            if (this.isVersionPage) {
                prefix = this.$t('price.listing-price-from');
            }

            return prefix;
        },
        hasOffers() {
            return this.offers.length > 0;
        },
        showColorNameOnOffersTab() {
            return this.isOffersTab && this.showColorName;
        },
        showPrice() {
            return (
                this.isVersionPrice ||
                (!this.isVersionPrice && (!this.isOffersTab || (this.isOffersTab && this.selectedOffer)))
            );
        },
        priceToShow() {
            return this.isOfferPrice
                ? this.selectedOffer.finalPrice
                : this.isVersionPrice
                    ? this.priceWithColor
                    : this.price;
        },
        tabsKey() {
            return JSON.stringify(this.tabs);
        },
        isVersionPage() {
            return this.formId === 'version';
        },
        isOfferPrice() {
            return this.selectedOffer && this.selectedOffer.finalPrice && !this.price;
        },
        isVersionPrice() {
            return this.isVersionPage && this.isRealColor;
        },
        priceWithColor() {
            const price = parseInt(this.price.replace(/[^\d]/g, ''), 10) + this.activeColor.basePrice;
            return this.dkCurrency(price, this.$i18n.locale);
        },
        projectId() {
            return dealerSelectionStore.state.projectId;
        },
        isRealColor() {
            return !isEmpty(this.activeColor);
        },
        colorTabName() {
            return this.isVersionPage
                ? this.$t('wizard-lead-form.color-and-optionals')
                : this.$t('wizard-lead-form.color-and-gearbox');
        },
    },
    mounted() {
        EventBus.$on('DkLeadForm:has-dealer-selection', this.handleDealerSelection);
        EventBus.$on('DkLeadForm:submit-disabled-change', this.updateSubmitDisabled);

        this.handleDefaultTab();
        this.fetchCarConfiguration();

        if (this.isMobile) {
            window.addEventListener('popstate', this.handleHashNavigation);

            this.handleModalSizeOnMobile();
        }
    },
    beforeDestroy() {
        EventBus.$off('DkLeadForm:has-dealer-selection', this.handleDealerSelection);
        EventBus.$off('DkLeadForm:submit-disabled-change', this.updateSubmitDisabled);

        dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_SELECTED_DEALER, null);

        if (this.isMobile) {
            window.removeEventListener('popstate', this.handleHashNavigation);
        }

        history.replaceState(null, document.title, window.location.pathname + window.location.search);
    },
    methods: {
        dkCurrency,
        fetchCarConfiguration() {
            fetchConfigurator(this.make, this.model)
                .then(({ data }) => {
                    if (isEmpty(this.selectedColor)) {
                        this.colors = ((data.colors && data.colors.external) || []).map((color) => {
                            if (color.active) {
                                this.activeColor = color;
                            }
                            return color;
                        });
                    } else {
                        this.colors = ((data.colors && data.colors.external) || []).map((color) => {
                            if (color.active && this.selectedColor.equipmentId !== color.equipmentId) {
                                color.active = false;
                            }
                            if (color.equipmentId === this.selectedColor.equipmentId) {
                                color.active = true;
                            }

                            return color;
                        });
                    }

                    this.colorImage =
                        data.colors &&
                        data.colors.external &&
                        data.colors.external.length &&
                        data.colors.external[0].colorImage &&
                        data.colors.external[0].colorImage.image800
                            ? data.colors.external[0].colorImage.image800
                            : data.imageOriginal;

                    this.areColorsLoaded = true;
                })
                .catch((error) => {
                    this.error = error;
                })
                .finally(() => {
                    if (this.isLeadFormMounted && this.loading) {
                        this.loading = false;
                    }
                });
        },
        onColorChange(color) {
            this.activeColor = color;
        },
        setActiveByPropertyAndIndex(property, index) {
            const foundItem = (this[property] && this[property][index]) || null;

            if (!foundItem) {
                return;
            }

            this[property] = this[property].map((item) => ({ ...item, active: false }));
            this.$set(this[property], index, { ...foundItem, active: true });

            return foundItem;
        },
        onFakeColorSelection(color) {
            this.currentColor = color;
        },
        onContinue(e) {
            this.isClickOnContinue = true;
            if (this.isPriceTab) {
                EventBus.$emit('WizardLeadForm:submit', e);
                this.isFormBusy = true;
                return;
            }

            if (this.isOffersTab && !this.showColorName) {
                this.showColorName = true;
            }

            this.isUpdatingTabs = true;

            let nextTab = this.tabKeys[0];

            if (this.activeTab) {
                const currentIndex = this.tabKeys.indexOf(this.activeTab);
                if (currentIndex !== -1 && currentIndex + 1 < this.tabKeys.length) {
                    nextTab = this.tabKeys[currentIndex + 1];
                }
            }

            if (nextTab !== this.activeTab) {
                this.changeCurrentTab(nextTab);

                if (this.isMobile) {
                    this.handleModalSizeOnMobile();
                }
            }

            this.isUpdatingTabs = false;
        },
        onTabChange({ tab }) {
            if (!this.isUpdatingTabs) {
                this.activeTab = tab.id;
                this.isClickOnTab = true;
                window.location.hash = `#${tab.id}`;
            }
        },
        handleDefaultTab() {
            if (!this.activeTab) {
                const defaultHash = this.tabKeys[0];
                this.activeTab = defaultHash;
                window.location.hash = defaultHash;
            }
        },
        // the following methods are needed to adjust the height of the modal
        // because it's scrollable on all tabs, except the dealer one
        registerModalHeight() {
            this.modalHeight = window.innerHeight;
        },
        handleModalSizeOnMobile() {
            const modalContainer = document.querySelector('.v--modal-box');
            const modalOverlay = document.querySelector('.v--modal-overlay');
            if (!modalContainer || !modalOverlay) {
                return;
            }

            this.registerModalHeight();

            const isFixedHeight = this.offers.length < 2 ? this.isDealerTab || this.isOffersTab : this.isDealerTab;

            modalContainer.style.height = isFixedHeight ? `${this.modalHeight}px` : 'auto';

            if (!isFixedHeight) {
                modalOverlay.classList.add('scrollable');
            } else if (modalOverlay.classList.contains('scrollable')) {
                modalOverlay.classList.remove('scrollable');
            }
        },
        handleHashNavigation(e) {
            if (this.isClickOnContinue) {
                this.isClickOnContinue = false;
                return;
            }

            this.isUpdatingTabs = true;

            const currentHash = (e.target.location.hash || '').replace('#', '');
            const tab = this.determineTabFromHash(currentHash);

            if (!this.isClickOnTab) {
                this.setTab(tab);
            }

            if (this.isMobile) {
                this.handleModalSizeOnMobile();
            }

            this.resetFlags();
        },

        determineTabFromHash(hash) {
            if (!hash) {
                this.hideModal();
                return this.tabKeys[0];
            }

            if (hash === this.tabKeys[0]) {
                return this.tabKeys[0];
            }

            if (hash === this.tabKeys[1]) {
                return this.tabKeys[1];
            }

            if (this.hasDealerSelection && hash === this.tabKeys[2]) {
                return this.selectedDealer ? this.tabKeys[2] : this.tabKeys[1];
            }

            return '';
        },
        resetFlags() {
            this.isUpdatingTabs = false;
            this.isClickOnTab = false;
        },
        setTab(tab, e = {}) {
            this.activeTab = tab;
            this.$refs.wizardNavigation && this.$refs.wizardNavigation.selectTab(`#${tab}`, e);
        },
        updateSubmitDisabled(value) {
            this.isSubmitDisabled = value;

            if (this.isFormBusy && !this.isSubmitDisabled) {
                this.isFormBusy = false;
            }
        },
        changeCurrentTab(tab) {
            this.setTab(tab);
            window.location.hash = `#${tab}`;
        },
        getDealerData() {
            const zipCode = sessionStorage.getItem('zipCode');
            const selectedDealer = sessionStorage.getItem('selectedDealer');
            const projectId = sessionStorage.getItem('projectId');

            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_ZIP_CODE, zipCode);
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_SELECTED_DEALER, JSON.parse(selectedDealer));
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_PROJECT_ID, projectId);
        },
        handleOfferSelection(index, offer) {
            if (this.selectedOffer && offer.offerId === this.selectedOffer.offerId) {
                return;
            }

            this.selectedOffer = offer;
            this.setSelectedOffer(offer, this.versionName);
        },
        isOfferSelected(offerId) {
            if (!this.selectedOffer) {
                return false;
            }
            return offerId === this.selectedOffer.offerId;
        },
        getOfferClasses(offer) {
            let classes = this.isOfferSelected(offer.offerId)
                ? 'tw-border-brand-primary tw-bg-primary-light tw-text-brand-primary'
                : 'tw-border-border-color';

            if (!isEmpty(offer.offerLogo)) {
                classes += ' tw-flex-col tw-items-start tw-gap-2 lg:tw-gap-3';
            } else {
                classes += ' tw-justify-between tw-items-center';
            }

            return classes;
        },
        isTabDisabled(tabId) {
            // if there is an offer tab, all tabs are disabled until an offer is selected.
            // if there is a dealer tab, the price tab is disabled until a dealer is selected.
            switch (tabId) {
                case 'wizard_form__price':
                    return (this.hasOffers && !this.selectedOffer) || (this.hasDealerSelection && !this.selectedDealer);
                case 'wizard_form__color':
                case 'wizard_form__dealer':
                    return this.hasOffers && !this.selectedOffer;
                default:
                    return false;
            }
        },
        onScrollChanged(scrollData) {
            const { tabId, hasScroll } = scrollData;

            if (!tabId) {
                return;
            }

            if (Object.prototype.hasOwnProperty.call(this.scrollStatus, tabId)) {
                this.$set(this.scrollStatus, tabId, hasScroll);
            }
        },
        hasScroll(tabId) {
            return this.scrollStatus[tabId];
        },
        handleDealerSelection(hasDealerSelection) {
            this.hasDealerSelection = hasDealerSelection;
            this.isLeadFormMounted = true;

            if (this.loading && this.areColorsLoaded) {
                this.loading = false;
            }

            if (this.showPriceTab) {
                this.changeCurrentTab('wizard_form__price');

                if (this.hasDealerSelection) {
                    this.getDealerData();
                }
            }

            this.$nextTick(() => {
                if (this.skipColorsTab) {
                    const colorsTabIndex = this.tabKeys.indexOf('wizard_form__color');
                    if (colorsTabIndex === 0) {
                        this.changeCurrentTab(this.tabKeys[1]);
                    }
                }
            });
        },
        isEmpty(value) {
            return isEmpty(value);
        },
        getTooltipData(offer) {
            return offer.promo_id && offer.tooltipObject && !isEmpty(offer.tooltipObject) ? offer.tooltipObject : {};
        },
    },
};
</script>
